import { useRef } from "react";
import type { LinkCardCanvasObject } from "@refinement-canvas/domain";
import { usesCrosshairCursor, useCanvasStore } from "../store/canvas-store";
import { useObjectDrag } from "../input/use-object-drag";
import { useCanvasOpDispatcherContext } from "../dispatcher-context";

interface Props {
  readonly object: LinkCardCanvasObject;
}

// OGP リンクカード。
// リッチ（画像 + タイトル）/ ミニマム（タイトルのみ）/ テキストのみ の 3 段階フォールバックで描画する。
// クリックで選択、ドラッグで移動できる。リンクは Ctrl/Cmd + クリックで新規タブを開く挙動に切り替える
// （ドラッグ移動 UX とリンク遷移 UX が衝突するため、明示的なキー操作を要求する）。
export function LinkCardObject({ object }: Props): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);
  const dispatcher = useCanvasOpDispatcherContext();
  const activeTool = useCanvasStore((s) => s.tool);
  // 参照専用（埋め込み）ではドラッグ移動が無効なため、プレーンクリックでリンクを開く。
  const readOnly = useCanvasStore((s) => s.readOnly);
  // 単一選択時だけでなく、選択集合（Ctrl+クリックや範囲指定での複数選択を含む）に
  // 入っていればハイライトする。
  const isSelected = useCanvasStore((s) => s.selectedObjectIds.includes(object.id));

  useObjectDrag({
    objectId: object.id,
    elementRef: ref,
    dispatcher,
    initialX: object.x,
    initialY: object.y,
    initialW: object.w,
    initialH: object.h,
    initialRotation: object.rotation,
    disabled: activeTool === "draw-pen",
  });

  const hasImage = Boolean(object.imageUrl);

  const onClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    // 参照専用ではプレーンクリックで遷移。編集モードではドラッグ移動と衝突しないよう
    // 従来どおり Ctrl/Cmd + クリックのときだけ遷移する。
    if (readOnly || event.ctrlKey || event.metaKey) {
      // 多層防御: 既存データに javascript: / data: 等の危険スキームが残っていても遷移しない。
      // 正規の link-card は常に http/https のため通常操作には影響しない。
      if (!isHttpUrl(object.url)) return;
      window.open(object.url, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      ref={ref}
      data-testid={`canvas-object-${object.id}`}
      data-object-type="link-card"
      onClick={onClick}
      style={{
        position: "absolute",
        left: `${object.x}px`,
        top: `${object.y}px`,
        width: `${object.w}px`,
        height: `${object.h}px`,
        zIndex: object.zOrder,
        background: "var(--color-surface)",
        // 選択枠は FigJam 風の水色で、ズーム倍率に依らず画面上一定の太さ（≒2px）にする（box-sizing:border-box のためレイアウトは崩れない）。
        border: isSelected
          ? "calc(2px * var(--canvas-inv-zoom, 1)) solid var(--color-selection)"
          : "1px solid var(--color-border)",
        borderRadius: "var(--radius-2)",
        boxShadow: "var(--shadow-1)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        textDecoration: "none",
        color: "var(--color-text)",
        transform: object.rotation ? `rotate(${object.rotation}deg)` : undefined,
        transformOrigin: "center",
        // 参照専用はリンクを開けることを示す pointer。編集時は配置ツール・ペン描画中 crosshair・通常 move。
        cursor: readOnly ? "pointer" : usesCrosshairCursor(activeTool) ? "crosshair" : "move",
        pointerEvents: activeTool === "draw-pen" ? "none" : "auto",
        userSelect: "none",
      }}
      title={
        readOnly
          ? `${object.url}\n（クリックで新規タブを開きます）`
          : `${object.url}\n（Ctrl/Cmd + クリックで新規タブを開きます）`
      }
    >
      {hasImage ? (
        <img
          src={object.imageUrl}
          alt=""
          style={{ width: "100%", height: "55%", objectFit: "cover", pointerEvents: "none" }}
          loading="lazy"
          referrerPolicy="no-referrer"
          draggable={false}
        />
      ) : null}
      <div style={{ padding: "12px", flex: 1, display: "flex", flexDirection: "column", gap: "4px" }}>
        <strong style={{ fontSize: "14px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {object.title}
        </strong>
        {object.description ? (
          <span style={{ fontSize: "12px", color: "var(--color-text-muted)" }}>
            {object.description}
          </span>
        ) : null}
        <span style={{ fontSize: "11px", color: "var(--color-text-muted)" }}>
          {object.siteName ?? safeHostname(object.url)}
        </span>
      </div>
    </div>
  );
}

function safeHostname(url: string): string {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
}

// window.open に渡してよい URL か（http / https のみ）。
function isHttpUrl(url: string): boolean {
  try {
    const protocol = new URL(url).protocol;
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}
